import { setInfoWindowContent } from './index';
import getMarkerIcons from './markerIcons';
// UMD build is ES5 with its dependencies bundled, which webpack 4 can parse without transpiling
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer/dist/index.umd';

const CLUSTER_SIZE = 62;

const getMarkerSize = (markerIcon) => ({
    pin: [37, 52],
    pinSelected: [37, 52],
    search: [47, 47]
}[markerIcon]);

const createIcon = (markerIcon, googleMapsApi, icons) => ({
    url: icons[markerIcon],
    size: new googleMapsApi.Size(...getMarkerSize(markerIcon)),
    scaledSize: new googleMapsApi.Size(...getMarkerSize(markerIcon)),
    origin: new googleMapsApi.Point(0, 0),
    anchor: new googleMapsApi.Point(0, 0)
});

export const createMarker = (markerIcon, markerData, googleMapsApi, map, icons = getMarkerIcons(map.getDiv())) => {
    const marker = new googleMapsApi.Marker({
        ...markerData,
        icon: createIcon(markerIcon, googleMapsApi, icons),
        optimized: false,
        map
    });

    return marker;
};

const resetMarkersSelectedState = (markers, googleMapsApi, icons) => {
    markers.forEach((marker) => {
        marker.setIcon(createIcon('pin', googleMapsApi, icons));
    });
};

const addMarkers = (
    map,
    googleMapsApi,
    places,
    onInfoWindowClose,
    onMarkerClick
) => {
    const icons = getMarkerIcons(map.getDiv());
    const markers = places.map((place) => {
        const infoWindow = new googleMapsApi.InfoWindow();
        const markerData = {
            position: {
                lat: place.Latitude,
                lng: place.Longitude
            },
            ...place
        };
        const marker = createMarker(
            'pin',
            markerData,
            googleMapsApi,
            map,
            icons
        );
        infoWindow.setContent(setInfoWindowContent(markerData));
        marker.infoWindow = infoWindow;
        // Event triggered when info window has opened
        googleMapsApi.event.addListener(infoWindow, 'domready', () => {
            resetMarkersSelectedState(markers, googleMapsApi, icons);
            marker.setIcon(createIcon('pinSelected', googleMapsApi, icons));
        });
        googleMapsApi.event.addListener(infoWindow, 'closeclick', (e) => {
            resetMarkersSelectedState(markers, googleMapsApi, icons);
            onInfoWindowClose(e);
        });
        return marker;
    });

    markers.forEach((marker) => {
        googleMapsApi.event.addListener(marker, 'click', () => {
            onMarkerClick(marker);
            // set clicked marker icon to selected
            marker.infoWindow.open(map, marker);
            map.setCenter(marker.getPosition());
        });
    });

    const clusterer = new MarkerClusterer({
        map,
        markers,
        // No clustering above maxZoom, so markers at the same address are shown separately
        // after clicking their cluster (the default click handler zooms to the cluster bounds)
        algorithm: new SuperClusterAlgorithm({ radius: 60, maxZoom: 16 }),
        renderer: {
            render: ({ count, position }) => new googleMapsApi.Marker({
                position,
                icon: {
                    url: icons.cluster,
                    size: new googleMapsApi.Size(CLUSTER_SIZE, CLUSTER_SIZE),
                    scaledSize: new googleMapsApi.Size(CLUSTER_SIZE, CLUSTER_SIZE),
                    anchor: new googleMapsApi.Point(CLUSTER_SIZE / 2, CLUSTER_SIZE / 2)
                },
                label: {
                    text: String(count),
                    color: '#000000',
                    fontSize: '12px'
                },
                // Clusters above single markers
                zIndex: Number(googleMapsApi.Marker.MAX_ZINDEX) + count
            })
        }
    });

    return { markers, clusterer };
};

export default addMarkers;
